import { If, Choose, When, Otherwise } from 'tsx-control-statements/components';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { Trans } from 'react-i18next';
import React, { ReactElement, useState } from 'react';
import classNames from 'classnames/bind';

import { selectCountry } from 'redux/User/selectors';

import { BREAK_POINTS, NOTIFY_STATUS } from 'constants/values';
import { DISCLAIMER_LINKS } from 'constants/links';
import { DEFAULT_COUNTRY } from 'constants/countriesData';

// linComponents
import { Box, Text, CheckBox } from 'libComponents';
import StartPageFooter from 'components/StartPageFooter';
import StartPageCard from 'components/StartPageCard';
import Notification from 'components/Notification';
import FooterWithPolicy from 'components/FooterWithPolicy';

import styles from './StartAgeLibContainer.module.css';

import { StartAgeLibContainerProps } from './types';

const cx = classNames.bind(styles);

function StartAgeLibContainer({
    pageTitle,
    pageTitleValues,
    cardsData,
    toNextPage,
    noScroll,
    noFooter,
    hasSubtitle,
    hasQuizSubtitle,
    disclaimer,
}: StartAgeLibContainerProps): ReactElement {
    const [isChecked, setIsChecked] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [isToastShown, setIsToastShown] = useState(false);

    const deviceWidth = window.screen.width;
    const isTablet = deviceWidth >= BREAK_POINTS.tablet && deviceWidth < BREAK_POINTS.desktop;
    const isDesktop = deviceWidth >= BREAK_POINTS.desktop;

    const mainTitleMarginBottom = isTablet ? 48 : 24;

    const userFromUS = useSelector(selectCountry) === DEFAULT_COUNTRY;
    const isCheckbox1 = disclaimer === 'checkbox1';
    const hasCheckbox = userFromUS && (isCheckbox1 || disclaimer === 'checkbox2');

    const scrollToError = () => {
        const target = document.getElementById('checkboxWrapper');

        const finalOffset = (target?.getBoundingClientRect().top || 0) + window.scrollY;

        window.scrollTo({
            top: finalOffset,
            behavior: 'smooth',
        });
    };

    const handleNextPage = (title: string, index: number) => {
        if (hasCheckbox) {
            if (isChecked) {
                toNextPage(title, index);
            } else {
                if (!isToastShown) {
                    setHasError(true);
                    toast(<Notification title="startPages.startAgeFullConfA.toast" status={NOTIFY_STATUS.ERROR} />);
                    setIsToastShown(true);
                }
                scrollToError();
            }
        } else {
            toNextPage(title, index);
        }
    };

    return (
        <div className={cx({ noScroll: noScroll })}>
            <Box paddingX={16} className={styles.container} paddingTop={80} paddingBottom={isTablet ? 71 : 16}>
                <Text
                    center
                    color="text-secondary"
                    type={isTablet ? 'h2' : 'h4'}
                    mb={hasSubtitle ? 8 : mainTitleMarginBottom}
                >
                    <Trans
                        i18nKey={pageTitle}
                        values={pageTitleValues}
                        components={{
                            title: <span className={hasSubtitle ? styles.title : undefined} />,
                            subtitle: <span className={hasSubtitle ? styles.subTitle : undefined} />,
                        }}
                    />
                </Text>
                {hasQuizSubtitle && (
                    <Text color="text-secondary" bold center mb={mainTitleMarginBottom}>
                        <Trans i18nKey="startPages.startAgeFullConf.quizSubtitle" />
                    </Text>
                )}
                <div className={styles.cardsBox}>
                    {cardsData.map((card, index) => {
                        return (
                            <StartPageCard
                                onClick={() => {
                                    handleNextPage(card.title, index);
                                }}
                                key={index}
                                title={card.title}
                                img={card.img}
                                dataLocator="ageButton"
                            />
                        );
                    })}
                </div>
                <div className={styles.policyFooterWrapper}>
                    <Choose>
                        <When condition={disclaimer === 'new'}>
                            <Text type="caption" color="text-secondary-subdued" center>
                                <Trans
                                    i18nKey="startPages.startAgeFullConfA.disclaimer.new"
                                    components={DISCLAIMER_LINKS}
                                />
                            </Text>
                        </When>
                        <When condition={hasCheckbox}>
                            <div className={styles.policyWrapper} id="checkboxWrapper">
                                <div data-locator="startPageConsentCheckbox">
                                    <CheckBox
                                        name="policy"
                                        large
                                        onChange={(name, value) => {
                                            setIsChecked(value);
                                            setHasError(false);
                                        }}
                                        className={hasError ? styles.checkboxError : undefined}
                                    />
                                </div>
                                <div>
                                    <Text type="caption" color="text-secondary">
                                        <Trans
                                            i18nKey={
                                                isCheckbox1
                                                    ? 'startPages.startAgeFullConfA.disclaimer.checkbox2'
                                                    : 'onboardingFlow.policyConsent.policy'
                                            }
                                            components={DISCLAIMER_LINKS}
                                        />
                                    </Text>
                                    {hasError && (
                                        <Text type="caption" color="critical-default">
                                            <Trans i18nKey="startPages.startAgeFullConfA.toast" />
                                        </Text>
                                    )}
                                </div>
                            </div>
                        </When>
                        <Otherwise>
                            <StartPageFooter />
                        </Otherwise>
                    </Choose>
                </div>
            </Box>
            <If condition={!noFooter}>
                <FooterWithPolicy isDesktop={isDesktop} />
            </If>
        </div>
    );
}

export default StartAgeLibContainer;
